import { CreatorOptions, CreatorSnapshot } from '@/types/creator';
import { ProjectUpload } from '@/types/uploads';
import { BoxModelStyle } from '@/three/types';

const AUTOSAVE_KEY = 'neopak_creator_autosave';

const isBrowserEnvironment = () => typeof window !== 'undefined';

const sanitizeSnapshot = (snapshot: unknown): CreatorSnapshot | null => {
	if (!snapshot || typeof snapshot !== 'object') return null;
	const typedSnapshot = snapshot as Partial<CreatorSnapshot>;
	const uploads = Array.isArray(typedSnapshot.uploads)
		? typedSnapshot.uploads
				.map((upload) => {
					if (!upload || typeof upload !== 'object') return null;
					const typedUpload = upload as Partial<ProjectUpload>;
					if (
						typeof typedUpload.name !== 'string' ||
						typeof typedUpload.mimeType !== 'string' ||
						typeof typedUpload.dataUrl !== 'string'
					) {
						return null;
					}

					return {
						id:
							typeof typedUpload.id === 'string'
								? typedUpload.id
								: globalThis.crypto?.randomUUID?.() ?? `upload-${Date.now()}`,
						name: typedUpload.name,
						size: typeof typedUpload.size === 'number' ? typedUpload.size : 0,
						mimeType: typedUpload.mimeType,
						dataUrl: typedUpload.dataUrl,
						source: (typedUpload.source as ProjectUpload['source']) ?? 'user-upload',
						uploadedAt:
							typeof typedUpload.uploadedAt === 'number'
								? typedUpload.uploadedAt
								: Date.now(),
					};
				})
				.filter((upload): upload is ProjectUpload => Boolean(upload))
		: undefined;

	const allowedStyles: BoxModelStyle[] = ['box-flap', 'box-die-cut', 'envelope', 'paper-bag', 'tape'];
	const snapshotStyle = typedSnapshot.selectedProductBoxStyle;
	const resolvedStyle = allowedStyles.includes(snapshotStyle as BoxModelStyle)
		? (snapshotStyle as BoxModelStyle)
		: 'box-flap';

	return {
		selectedProductTypeId: typedSnapshot.selectedProductTypeId ?? null,
		selectedProductBoxStyle: resolvedStyle,
		selectedVariantId: typedSnapshot.selectedVariantId ?? null,
		configuration: typedSnapshot.configuration ?? undefined,
		options: typedSnapshot.options as Partial<CreatorOptions> | undefined,
		pricing: typedSnapshot.pricing ?? undefined,
		textElements: typedSnapshot.textElements ?? undefined,
		imageElements: typedSnapshot.imageElements ?? undefined,
		uploads,
		savedAt: typedSnapshot.savedAt ?? null,
		apiProjectId: typedSnapshot.apiProjectId ?? null,
		isTemplateEditor: Boolean(typedSnapshot.isTemplateEditor),
		templateRequestId:
			typeof typedSnapshot.templateRequestId === 'string'
				? typedSnapshot.templateRequestId
				: null,
		isViewOnly: Boolean(typedSnapshot.isViewOnly),
	};
};

export const loadCreatorSnapshot = (): CreatorSnapshot | null => {
	if (!isBrowserEnvironment()) return null;
	try {
		const raw = window.localStorage.getItem(AUTOSAVE_KEY);
		if (!raw) return null;
		return sanitizeSnapshot(JSON.parse(raw));
	} catch (error) {
		if (import.meta.env.DEV) console.error('Autosave restore failed', error);
		return null;
	}
};

export const saveCreatorSnapshot = (snapshot: CreatorSnapshot) => {
	if (!isBrowserEnvironment()) return;
	try {
		window.localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(snapshot));
	} catch (error) {
		if (import.meta.env.DEV) console.error('Autosave failed', error);
	}
};

export const clearCreatorSnapshot = () => {
	if (!isBrowserEnvironment()) return;
	try {
		window.localStorage.removeItem(AUTOSAVE_KEY);
	} catch (error) {
		if (import.meta.env.DEV) console.error('Autosave clear failed', error);
	}
};
